import { type AlertProps as AntAlertProps, Flex, Space } from 'antd';
import type { FC, ReactNode } from 'react';
import { useTheme } from 'styled-components';
import type { StrictOmit } from '@enmon/commons';
import { TRANSLATED_ACTIONS } from '@/common/messages';
import { StyledAlert } from './components/StyledAlert';
import { getAlertIconByType } from './utils/getAlertIconByType';
import { Typography } from '../typography';
import { HeadlineLevel } from '../typography/enums/level';
import { Icon } from '../icon';
import { ButtonAnt, type ButtonAntProps } from '../buttons/button/ButtonAnt';
import { FormattedMessage } from '../formatted_message/FormattedMessage';

const { Headline, Paragraph } = Typography;

export enum AlertLayout {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

export type AlertProps = StrictOmit<AntAlertProps, 'action'> & {
  actionButtonProps?: {
    confirmText?: ReactNode | string;
    cancelText?: ReactNode | string;
    onConfirm?: () => void;
    onCancel?: () => void;
    layout?: AlertLayout;
    confirmButtonProps?: Partial<ButtonAntProps>;
    cancelButtonProps?: Partial<ButtonAntProps>;
  };
};

export const Alert: FC<AlertProps> = ({
  type,
  message,
  description,
  actionButtonProps,
  closable = false,
  ...restProps
}) => {
  const theme = useTheme();
  const {
    confirmText,
    cancelText,
    onConfirm,
    onCancel,
    layout = AlertLayout.Horizontal,
    confirmButtonProps,
    cancelButtonProps,
  } = actionButtonProps || {};

  return (
    <StyledAlert
      action={
        layout === AlertLayout.Horizontal && (
          <Flex vertical gap={8}>
            {onConfirm && (
              <ButtonAnt
                danger={type === 'error'}
                size="small"
                type="primary"
                {...confirmButtonProps}
                onClick={onConfirm}
              >
                {confirmText ?? <FormattedMessage {...TRANSLATED_ACTIONS.confirm} />}
              </ButtonAnt>
            )}
            {onCancel && (
              <ButtonAnt danger={type === 'error'} size="small" {...cancelButtonProps} onClick={onCancel}>
                {cancelText ?? <FormattedMessage {...TRANSLATED_ACTIONS.cancel} />}
              </ButtonAnt>
            )}
          </Flex>
        )
      }
      closable={closable}
      closeIcon={<Icon $fontSize="16px" color={theme.color.semantic.primary.background.default} iconString="close" />}
      description={
        (description || layout === AlertLayout.Vertical) && (
          <Space direction="vertical" size={16}>
            <Paragraph>{description}</Paragraph>
            {layout === AlertLayout.Vertical && (
              <Flex gap={8}>
                <ButtonAnt
                  danger={type === 'error'}
                  size="small"
                  type="primary"
                  {...confirmButtonProps}
                  onClick={onConfirm}
                >
                  {confirmText ?? <FormattedMessage {...TRANSLATED_ACTIONS.confirm} />}
                </ButtonAnt>
                <ButtonAnt danger={type === 'error'} size="small" {...cancelButtonProps} onClick={onCancel}>
                  {cancelText ?? <FormattedMessage {...TRANSLATED_ACTIONS.cancel} />}
                </ButtonAnt>
              </Flex>
            )}
          </Space>
        )
      }
      icon={
        <Flex align="center" style={{ height: '20px' }}>
          {getAlertIconByType(type, theme)}
        </Flex>
      }
      message={message && <Headline level={HeadlineLevel.Three}>{message}</Headline>}
      type={type}
      {...restProps}
    />
  );
};
